import { Injectable } from '@angular/core';
import {
    ApiDataStatus,
    fallbackIfDelayed,
    BlueCityService,
    type GetAllBlueCityProjectsRequestParams,
} from '@ui/shared/util-api';
import { BehaviorSubject, Observable, catchError, map, of, switchMap } from 'rxjs';
import { BlueCityItem } from '../../definitions/blue-city-item';
import { Result, ToResult, pipeDataResult, toDataResult } from '@team23/ts-result';

type ListDataResult = Result<Array<BlueCityItem>, ApiDataStatus>;

const reloadDelay = 500;

@Injectable({ providedIn: 'root' })
export class BlueCityListDataService {

    private readonly _reload$ = new BehaviorSubject<number>(0);

    constructor(
        private readonly _blueCityApi: BlueCityService,
    ) { }

    public getListItems$(
        params: {
            titleContains?: string;
            categoryIds?: Array<number>;
            sponsorIds?: Array<number>;
        },
    ): Observable<ListDataResult> {
        const requestParams: GetAllBlueCityProjectsRequestParams = {};

        if (params.titleContains) {
            requestParams.search = params.titleContains.toString();
        }

        if (params.sponsorIds?.length) {
            requestParams.sponsorIds = params.sponsorIds;
        }

        if (params.categoryIds?.length) {
            requestParams.categoryIds = params.categoryIds;
        }

        return this._reload$
            .pipe(
                switchMap(loadingDelay =>
                    fallbackIfDelayed(
                        this._blueCityApi.getAllBlueCityProjects(requestParams)
                            .pipe(
                                // Catch the error within the switchmap to keep the outer observable from closing.
                                catchError(() => of(ToResult.fromErrorKey(ApiDataStatus.GenericError))),
                            ),
                        ToResult.fromErrorKey(ApiDataStatus.Loading),

                        /*
                         * Whenever a reload is triggered, immediately show the loading status by delaying the query.
                         * This lets the UI show a visible reaction to the button even when the error is instantaneous.
                         */
                        loadingDelay ? { gracePeriod: 0, forcedDelay: loadingDelay } : undefined,
                    ),
                ),
                pipeDataResult(
                    map(result =>
                        result.data
                            .map(project => new BlueCityItem(project))
                            .sort((a, b) =>
                                (a.title === b.title
                                    ? 0
                                    : a.title > b.title
                                        ? 1
                                        : -1),
                            ),
                    ),
                    toDataResult(),
                ),
            );
    }

    public reloadListItems(): void {
        this._reload$.next(reloadDelay);
    }
}
